import posthog from 'posthog-js';

import type { CaptureResult } from 'posthog-js';
import type { CampaignTags } from './campaign-tags.js';

/** Route-derived properties (from the page being viewed) attached to every outgoing PostHog event. */
export interface AnalyticsRouteContext {
	org_name?: string;
	revision_id?: string;
}

let routeContextSource: () => AnalyticsRouteContext = () => ({});

/**
 * Registers the callback that supplies the current {@link AnalyticsRouteContext}.
 * The context is empty until a source is registered.
 *
 * @param source - Returns the context for the page currently being viewed.
 */
export function setAnalyticsRouteContextSource(source: () => AnalyticsRouteContext): void {
	routeContextSource = source;
}

/**
 * PostHog `before_send` hook that merges the current {@link AnalyticsRouteContext}
 * into every outgoing event's properties. Properties already set on the event win.
 *
 * @param event - The event PostHog is about to send, or null when already dropped.
 * @returns The enriched event; null is passed through.
 */
export function withAnalyticsRouteContext(event: CaptureResult | null): CaptureResult | null {
	if (event === null) {
		return null;
	}
	event.properties = { ...routeContextSource(), ...event.properties };
	return event;
}

/**
 * Which workbench surface a workbench event came from. Workbench lives on several surfaces, so
 * every workbench event carries this rather than being split into per-surface names.
 */
export type WorkbenchAnalyticsSource =
	| 'results_chat'
	| 'wizard_chat'
	| 'package_family_chat'
	| 'datasheet_chat'
	| 'agent_conversations';

/** How a user rated one assistant reply. */
export type WorkbenchFeedbackRating = 'positive' | 'negative';

/**
 * What the user was looking at when a workbench event fired, attached to all of them.
 * Every field is optional because the surfaces describe different things — a
 * design revision, a package under review, a datasheet — and each fills in only
 * what it has. Identifiers and catalogue names only; nothing from the design
 * itself, and never any workbench text.
 */
export interface WorkbenchSurfaceContext {
	project_id?: string;
	revision_id?: string;
	/** Results page: the panel open beside the workbench (`bom`, `design-explorer`, `block-diagram`). */
	open_panel?: string;
	/** Wizard: config name of the step being reviewed. */
	wizard_step?: string;
	/** The part under discussion, where the surface is about one. */
	package_mpn?: string;
	package_id?: string;
	package_family?: string;
	manufacturer?: string;
}

/** The verdict a submitted upload reached. */
export enum UploadSubmitOutcome {
	/** The revision was finalized and has moved on to processing. */
	Finalized = 'finalized',
	/** The backend read the file set and refused it. */
	Rejected = 'rejected',
	/** The submission never reached a verdict — offline, or the revision was gone. */
	Failed = 'failed',
}

/**
 * The shape of one client-side upload validation outcome, as carried by the upload validation
 * events. Rule identifiers and counts only: no file names or paths.
 */
export interface UploadValidationAnalyticsSummary {
	/** The tool the user declared, e.g. `altium`, or null before one is picked. */
	declared_format: string | null;
	/** The tool the validator detected, or null when detection was ambiguous or failed. */
	detected_format: string | null;
	/** Files in the validated set. */
	file_count: number;
	/** Distinct `errorType` values on Error-severity findings, sorted. */
	error_types: string[];
	/** Distinct `errorType` values on Warning-severity findings, sorted. */
	warning_types: string[];
	/** Distinct required-file rule ids among the findings, sorted. */
	rule_ids: string[];
	error_count: number;
	warning_count: number;
}

/**
 * Properties of the `user_signed_up` event. The campaign tags are flat properties, so a destination can map each one
 * to a field without unpacking a nested object.
 */
export interface UserSignedUpPayload extends CampaignTags {
	// Required (not ambient): the auth callback route has no org param.
	org_name: string;
	/** Whether a new organization was created (vs joining an existing one). */
	org_created: boolean;
	role: string;
	/**
	 * Who signed up, taken from the Auth0 token. Present on every normal signup; absent only if the token carries no
	 * email claim. Named on the event because it fires before the person is identified, so a CRM destination has
	 * nothing else to key a contact on.
	 */
	email?: string;
	/**
	 * The signup's campaign tags as person properties (under a `signup_` prefix), set only if not already set. Absent
	 * when the signup carried no tags.
	 */
	$set_once?: Record<string, string>;
}

/**
 * Typed catalogue of the custom product-analytics events the frontend emits to
 * PostHog. Each key is the exact event name sent to PostHog (snake_case); the
 * value type is the event-specific property bag, exclusive of the ambient
 * {@link AnalyticsRouteContext} every event additionally carries. Declare a
 * context key here (as required) only when the firing page's route lacks it.
 *
 * Centralising the catalogue keeps event names and property shapes consistent
 * across the app and gives every call site compile-time checking of its
 * payload. Route all custom events through {@link captureAnalyticsEvent} rather
 * than calling `posthog.capture` directly.
 *
 * PostHog's reserved events (prefixed with `$`, e.g. `$pageview`) are captured
 * directly and intentionally excluded from this catalogue.
 */
export interface AnalyticsEventPayloads {
	/** A user landed on the analysis results page and its data finished loading. */
	results_viewed: {
		project_id: string;
		revision_id: string;
		/** Active results tab, e.g. `analysis-results`, `ai-findings`. */
		tab: string;
		/** Total (unfiltered) result count for the selected requirement types. */
		total_result_count: number;
		/** Count of results per computed status, e.g. `{ success: 12, error: 3 }`. */
		status_breakdown: Record<string, number>;
		/** Whether a column filter was active when the page settled. */
		is_filtered: boolean;
	};
	/** The server accepted a customer's BOM review submission. */
	bom_review_submitted: {
		revision_id: string;
	};
	/** A user opened a single analysis result's detail panel. */
	analysis_result_opened: {
		project_id: string;
		revision_id: string;
		result_id: string;
		/** Computed status of the opened result. */
		status: string;
	};
	/** The results route rendered a non-ready state instead of results. */
	results_unavailable: {
		project_id: string;
		revision_id: string;
		revision_status: string;
		reason: 'error' | 'processing';
	};
	/** A user opened a workbench panel. */
	chat_opened: WorkbenchSurfaceContext & {
		source: WorkbenchAnalyticsSource;
	};
	/**
	 * A user submitted a message in a workbench composer. Carries the message's shape
	 * only: the text itself is deliberately never sent, so the design questions
	 * customers ask stay out of analytics.
	 */
	chat_message_sent: WorkbenchSurfaceContext & {
		source: WorkbenchAnalyticsSource;
		/** Characters in the submitted message. */
		message_length: number;
		/** 1-based position of this message among the thread's user messages. */
		message_index: number;
	};
	/** A workbench run started by a submitted message settled, with a reply or an error. */
	chat_response_received: WorkbenchSurfaceContext & {
		source: WorkbenchAnalyticsSource;
		/** Milliseconds from the message being submitted to the run settling. */
		latency_ms: number;
		/** Characters in the reply; 0 when the run produced none. Text is never sent. */
		response_length: number;
		is_error: boolean;
	};
	/**
	 * A user rated one assistant reply thumbs up or down. Carries the ids needed
	 * to pull the rated reply up afterwards, never the reply itself.
	 */
	chat_feedback_submitted: WorkbenchSurfaceContext & {
		source: WorkbenchAnalyticsSource;
		rating: WorkbenchFeedbackRating;
		/** The rated message, locating it within the conversation. */
		message_id: string;
		/** Conversation holding the reply; absent on a thread not yet persisted. */
		conversation_id?: string;
	};
	/** A user declared which CAD/EDA tool their project was authored in, in the upload flow. */
	project_format_selected: {
		project_id: string;
		format: 'altium' | 'orcad' | 'kicad' | 'fusion360' | 'xpedition' | 'other';
	};
	files_selected_for_upload: {
		project_id: string;
		file_count: number;
		file_names: string[];
	};
	/**
	 * A submitted upload reached a verdict. Fires once per press of Submit, and is the
	 * only upload event the transfer itself produces — a session moving a hundred files
	 * reports one of these, not one per file.
	 */
	upload_submitted: {
		project_id: string;
		revision_id: string;
		outcome: UploadSubmitOutcome;
		/** The tool the user declared, or null for the "other" format. */
		declared_format: string | null;
		file_count: number;
		file_names: string[];
		/**
		 * Files whose transfer failed at least once before the submission. Submit is gated
		 * on every file having landed, so on a submitted upload these were all retried
		 * back to success — a count above zero marks a session that had to fight for it.
		 */
		transfer_failure_count: number;
		/** Milliseconds from confirming the submission to its verdict. */
		duration_ms: number;
		/** Root cause of a `failed` outcome; absent on the other two. */
		error_message?: string;
	};
	/**
	 * Client-side validation of the staged files reached Error severity, gating the upload. Fires
	 * once when an upload becomes blocked and again whenever the set of blocking findings changes
	 * while it stays blocked. Carries rule identifiers and counts only, never file names.
	 */
	upload_validation_blocked: UploadValidationAnalyticsSummary & {
		project_id: string;
		revision_id: string;
		/** 1-based position of this state since the upload was last unblocked; filter on 1 to count stretches. */
		attempt: number;
	};
	/** A previously blocked upload's files later validated without an Error, ungating the upload. */
	upload_validation_recovered: UploadValidationAnalyticsSummary & {
		project_id: string;
		revision_id: string;
		/** How many blocked states the session passed through before recovering. */
		blocked_attempts: number;
		/** The `error_types` of the last blocked state, i.e. what the user resolved. */
		resolved_error_types: string[];
	};
	project_created: {
		owner_id: string;
		project_name: string;
		revision_name: string;
	};
	project_created_error: {
		owner_id: string;
		error_message: string;
	};
	revision_created: {
		project_id: string;
		revision_name: string;
	};
	/** A first-login user completed self-serve provisioning (account created, org created or joined). */
	user_signed_up: UserSignedUpPayload;
	user_invited: {
		email: string;
		display_name: string;
	};
	wizard_inactivity_message_shown: Record<never, never>;
	wizard_inactivity_message_closed: Record<never, never>;
	/** A wizard operator toggled the workbench pane between the agent and the classic AI workbench. */
	wizard_chat_mode_switched: {
		/** The mode switched to. */
		mode: 'classic' | 'agent';
		/** The wizard step's config name, when one is in context. */
		step?: string;
	};
	/** A wizard step was claimed and rendered for the operator. */
	wizard_step_opened: {
		wizard_plan_id: string | null;
		wizard_step_id: string;
		wizard_step_type: string;
		package_id: string | null;
		package_mpn: string | null;
		wizard_generator_name: string | null;
	};
	/** The design-month gate stopped a new project and was shown to the customer. */
	design_month_gate_shown: {
		organization_id: string;
	};
	/** The customer opened the scheduler from the design-month gate. */
	design_month_gate_scheduler_opened: {
		organization_id: string;
	};
	/** The customer chose to email Cadstrom from the design-month gate. */
	design_month_gate_email_chosen: {
		organization_id: string;
	};
	/** The contact gate for a revision waiting on design review credit was shown. */
	credit_required_contact_shown: {
		organization_id: string | null;
		revision_id: string;
	};
	/** The customer opened the scheduler from the credit contact gate. */
	credit_required_contact_scheduler_opened: {
		organization_id: string | null;
		revision_id: string;
	};
	/** The customer chose to email Cadstrom from the credit contact gate. */
	credit_required_contact_email_chosen: {
		organization_id: string | null;
		revision_id: string;
	};
	/** A tRPC query resolved with an application-level failure (`isSuccess: false`). */
	query_error: {
		query_key: unknown;
		error_code: unknown;
		error_message: unknown;
	};
	/** A tRPC mutation resolved with an application-level failure (`isSuccess: false`). */
	mutation_error: {
		mutation_key: unknown;
		error_code: unknown;
		error_message: unknown;
	};
	/**
	 * An autosave mutation reported success, but the editor's draft still differs from the server
	 * state it returned — so the save silently didn't round-trip what the user is looking at.
	 * Carries only the shape of the disagreement: the states themselves routinely run past PostHog's
	 * 1 MB per-event limit, which drops the event outright.
	 */
	autosave_state_mismatch: {
		/** The autosave's mutation keys, identifying which editor reported the mismatch. */
		mutation_keys: string[];
		/** Differing paths from the state root, capped; see `differing_path_count` for the true total. */
		differing_paths: string[];
		/** Per-path detail as `path:kind:serverType->draftType`, capped the same way. */
		differing_details: string[];
		/**
		 * The actual values behind the first few differing paths, as `path: server -> draft`. Each value is
		 * truncated, and a container is rendered as its size rather than its contents, so no amount of
		 * package data can ride along.
		 */
		differing_samples: string[];
		differing_path_count: number;
		/** Whether the comparison stopped early, making `differing_path_count` a floor. */
		truncated: boolean;
		server_bytes: number;
		draft_bytes: number;
	};
	/**
	 * A re-entered wizard form step found saved form data it could not parse, so it rendered a
	 * reconstruction from the canonical output instead of what the operator left. Carries only where the
	 * parse failed, never the data.
	 */
	wizard_form_data_unparseable: {
		step_type: string;
		/** Zod issue paths, joined with dots, capped. */
		issue_paths: string[];
		issue_count: number;
	};
	/** Feature-flag resolution hit the load timeout and used the fallback value. */
	feature_flag_resolution_timeout: {
		flag_key: string;
		timeout_ms: number;
	};
	/** Auth0 silent token refresh failed (expected when there is no session). */
	auth_get_token_silently_failed: {
		error_code: string;
		error_description?: string;
		is_expected: boolean;
		cookie_enabled: boolean;
	};
}

/**
 * Full property bags as sent to PostHog: each event's payload plus the ambient
 * {@link AnalyticsRouteContext}. Context values passed explicitly win over the
 * ambient ones.
 */
export type AnalyticsEventProperties = {
	[E in keyof AnalyticsEventPayloads]: AnalyticsEventPayloads[E] & AnalyticsRouteContext;
};

/** Union of all valid custom analytics event names. */
export type AnalyticsEventName = keyof AnalyticsEventProperties;

/**
 * Argument tuple for capturing a given event: events with no required
 * properties take an optional bag (ambient context overrides only), everything
 * else requires its typed payload.
 */
export type AnalyticsEventArgs<E extends AnalyticsEventName> =
	Record<never, never> extends AnalyticsEventProperties[E] ? [properties?: AnalyticsEventProperties[E]]
	:	[properties: AnalyticsEventProperties[E]];

/**
 * Captures a custom analytics event on the shared PostHog client, with the
 * event name and payload checked against {@link AnalyticsEventProperties}.
 *
 * Safe to call anywhere (components, hooks, query functions) and before PostHog
 * is initialised: the client no-ops rather than throwing. In local dev the
 * event is dropped by `dropAnalyticsEventsInDev` before sending.
 *
 * @param event - The event name from the analytics catalogue.
 * @param args - The event's typed properties, omitted for property-less events.
 */
export function captureAnalyticsEvent<E extends AnalyticsEventName>(event: E, ...args: AnalyticsEventArgs<E>): void {
	posthog.capture(event, args[0]);
}
